// S5 · Step 3 — "Bind work centers" (Module 02 §4 S5).
//
// One row per pack ROUTING-STEP TEMPLATE × facility, each bound to a real work
// center. Without it the blocking go-live item `catalogue.routings_bound`
// (weight 8, §3.6) has no surface: a tenant's only route past the gate was for a
// group_admin to WAIVE a blocking item the product was supposed to let them fix.
//
// Contract (Module 01 §2.18):
//   GET /v1/catalogue/routing-step-bindings?facility_id=  → ground truth
//   PUT /v1/catalogue/routing-step-bindings               → REPLACES the set
//
// The GET is new in this PR precisely so the "unbound" badges are ground truth.
// Before it existed the only read was the PUT's own response, so a badge would
// have had to be derived from an unsaved client draft — a guess dressed as a
// verdict on a blocking gate.
//
// The PUT replaces the whole facility set, so the editor always sends every
// binding it means to keep: dropping a row here really does unbind it, and the
// panel says so rather than letting a partial send delete silently.
//
// §7: reading is `catalogue.structure.read`; writing is `catalogue.template.manage`
// (group_admin / facility_admin). The client mirror only hides controls — the
// server refuses regardless.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { StatusChip } from "../../../../components/status-chip";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { requestIdOf } from "../../../../lib/request";
import { useCataloguePermissions } from "../../../office/catalogue/hooks/permissions";
import { usePutRoutingStepBindings, useRoutingStepBindings } from "../../hooks/onboarding-queries";
import { useTerm } from "../../hooks/terminology";
import { WizardProblemNotice } from "../../components/wizard-notices";
import type { ExistingTree } from "./plant-draft";

type S = components["schemas"];

export interface StepTemplate {
  /** `step.extrusion` — the ref the pack's routing templates cite. */
  readonly ref: string;
  readonly label: string;
}

/** Unsaved bindings, tagged with the server answer they were made against. */
interface Edits {
  readonly base: string;
  readonly values: Record<string, string>;
}

/** Shared empty override set — one stable identity, so `draft`'s memo does not
 * rebuild on a pass that changed nothing. Never mutated; only spread from. */
const NO_EDITS: Record<string, string> = {};

export interface BindingsPanelProps {
  /** The COMMITTED tree — bindings point at real work-center ids, so a draft
   * work center that has never been committed cannot be bound yet. */
  tree: ExistingTree;
  /** Routing-step templates from the installed pack. */
  templates: readonly StepTemplate[];
  /** True when no pack is installed, so there is nothing to bind. */
  noPack: boolean;
  frozen: boolean;
}

export function BindingsPanel({ tree, templates, noPack, frozen }: BindingsPanelProps) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const perms = useCataloguePermissions();
  const facilities = tree.facilities.filter((f) => f.status === "active");

  /* RENDER-ONCE (design-system §5), the panel's own half of it.
   *
   * Everything this panel's LAYOUT depends on is DERIVED DURING RENDER. Nothing
   * that decides how tall this card is may be seeded by a mount effect, because
   * an effect runs after the commit that scheduled it — so the browser is free
   * to paint the pre-effect shape, and the post-effect shape then reflows the
   * page. That is precisely the class of defect the shell's permission gate was
   * added for (onboarding-layout.tsx, 2026-08-10, CLS 0.0004), and this panel
   * reopened it lower down the page: it painted with `facilityId === ""` (a
   * disabled query reads as `isPending`, so a 4-line skeleton) and then with an
   * unseeded `{}` draft (every template UNBOUND, so the `bindings-unbound`
   * warning line was present) before its two effects settled it one commit
   * later. Dropping that warning line moved `ul.plant-binding-list` up — CLS
   * 0.0004 @330ms on deployed staging, W-UX1-PLANT state 1.
   *
   * Both effects are gone. The panel now paints its settled shape first time. */

  /** Which facility is being bound. DERIVED — the screen above has already
   * warmed THIS facility's bindings behind its atomic-reveal gate, so deriving
   * the default means the panel's first render observes a cache entry that is
   * already populated and paints the real grid instead of a skeleton. A user
   * choice wins; a choice that no longer names a live facility does not. */
  const [chosenFacilityId, setChosenFacilityId] = useState<string | null>(null);
  const facilityId =
    chosenFacilityId !== null && facilities.some((f) => f.id === chosenFacilityId)
      ? chosenFacilityId
      : (facilities[0]?.id ?? "");

  const bindings = useRoutingStepBindings(facilityId);
  const save = usePutRoutingStepBindings();
  const canBind = perms.has("catalogue.template.manage") && !frozen;

  const workCenters = useMemo(
    () => tree.workCenters.filter((w) => w.status === "active" && w.facility_id === facilityId),
    [tree.workCenters, facilityId],
  );

  /** ref → work_center_id AS THE SERVER HOLDS IT, the baseline `dirty` and the
   * badges are measured against. */
  const serverKey = JSON.stringify(bindings.data?.items ?? []);
  const serverDraft = useMemo(() => {
    const items = JSON.parse(serverKey) as S["RoutingStepBinding"][];
    const next: Record<string, string> = {};
    for (const b of items) next[b.step_template_ref] = b.work_center_id;
    return next;
  }, [serverKey]);

  /** The user's UNSAVED overrides on top of the server's answer, tagged with the
   * answer they were made against. Overlaying instead of copying is what lets
   * the seeded state exist on the FIRST render; the tag is what drops stale
   * edits when a save (or a facility switch) brings a new answer back. */
  const [edits, setEdits] = useState<Edits>({ base: serverKey, values: NO_EDITS });
  const localEdits = edits.base === serverKey ? edits.values : NO_EDITS;
  if (edits.base !== serverKey) {
    // React's "adjusting state during render": the component re-runs
    // immediately, before children render and before the DOM is touched, so a
    // new server answer can never be painted with the old answer's edits on it.
    setEdits({ base: serverKey, values: NO_EDITS });
  }
  const draft = useMemo(() => ({ ...serverDraft, ...localEdits }), [serverDraft, localEdits]);

  const setBinding = (ref: string, workCenterId: string): void => {
    setEdits((prev) => ({
      base: serverKey,
      values: { ...(prev.base === serverKey ? prev.values : NO_EDITS), [ref]: workCenterId },
    }));
  };

  const unbound = templates.filter((tpl) => (draft[tpl.ref] ?? "") === "");
  const dirty = JSON.stringify(cleanBindings(draft)) !== JSON.stringify(cleanBindings(serverDraft));

  const boundCount = templates.length - unbound.length;

  if (noPack) {
    return (
      <Card
        title={t("onboarding.plant.bindings.heading")}
        className="plant-bindings"
        data-testid="plant-bindings"
      >
        <EmptyState
          title={t("onboarding.plant.bindings.noPackTitle")}
          body={t("onboarding.plant.bindings.noPackBody")}
        />
      </Card>
    );
  }

  return (
    <Card
      title={t("onboarding.plant.bindings.heading")}
      className="plant-bindings"
      data-testid="plant-bindings"
      action={
        templates.length > 0 && facilities.length > 0 && workCenters.length > 0 ? (
          /* §UX-1 state 5: per-step rows with ONE summary line. */
          <span className="plant-bindings-summary" data-testid="bindings-summary">
            <StatusChip variant={unbound.length === 0 ? "ok" : "warn"}>
              {t("onboarding.plant.bindings.summary", {
                bound: boundCount,
                total: templates.length,
              })}
            </StatusChip>
          </span>
        ) : undefined
      }
    >
      <div className="plant-bindings-body">
        <p className="plant-hint">
          {t("onboarding.plant.bindings.blurb", { workCenter: term("work_center") })}
        </p>

        {facilities.length === 0 ? (
          // Bindings reference COMMITTED work centers, so the plant map has to be
          // committed first — said plainly instead of showing an empty grid.
          <EmptyState
            title={t("onboarding.plant.bindings.commitFirstTitle")}
            body={t("onboarding.plant.bindings.commitFirstBody", { facility: term("facility") })}
          />
        ) : (
          <>
            <label className="ds-field plant-bindings-facility">
              <span>
                {t("onboarding.plant.bindings.facilityLabel", { facility: term("facility") })}
              </span>
              <select
                className="ds-select"
                value={facilityId}
                onChange={(e) => {
                  setChosenFacilityId(e.target.value);
                }}
              >
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name} ({f.code})
                  </option>
                ))}
              </select>
            </label>

            {bindings.isPending ? (
              <CardSkeleton lines={4} label={t("onboarding.plant.bindings.loading")} />
            ) : bindings.error !== null ? (
              <ErrorState
                title={t("onboarding.plant.bindings.errorTitle")}
                detail={t("onboarding.plant.bindings.errorBody")}
                retryLabel={t("onboarding.state.retry")}
                onRetry={() => void bindings.refetch()}
                requestId={requestIdOf(bindings.error)}
                requestIdLabel={t("onboarding.state.requestId")}
              />
            ) : templates.length === 0 ? (
              <EmptyState
                title={t("onboarding.plant.bindings.noTemplatesTitle")}
                body={t("onboarding.plant.bindings.noTemplatesBody")}
              />
            ) : workCenters.length === 0 ? (
              <EmptyState
                /* Both halves interpolate: the TITLE also carries
                   `{{workCenter}}`, and without values it rendered the raw
                   placeholder — "No {{workCenter}} yet" — on screen. */
                title={t("onboarding.plant.bindings.noWorkCentersTitle", {
                  workCenter: term("work_center"),
                })}
                body={t("onboarding.plant.bindings.noWorkCentersBody", {
                  workCenter: term("work_center"),
                })}
              />
            ) : (
              <>
                {unbound.length > 0 ? (
                  <p
                    className="plant-bindings-warning"
                    role="status"
                    data-testid="bindings-unbound"
                  >
                    {t("onboarding.plant.bindings.unboundWarning", { count: unbound.length })}
                  </p>
                ) : null}

                <ul className="plant-binding-list">
                  {templates.map((tpl) => {
                    const value = draft[tpl.ref] ?? "";
                    const id = `bind-${tpl.ref.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
                    return (
                      <li
                        className="plant-binding-row"
                        key={tpl.ref}
                        data-testid="plant-binding-row"
                      >
                        <label htmlFor={id}>
                          {tpl.label}
                          {value === "" ? (
                            <StatusChip variant="warn">
                              {t("onboarding.plant.bindings.unboundBadge")}
                            </StatusChip>
                          ) : (
                            <StatusChip variant="ok">
                              {t("onboarding.plant.bindings.boundBadge")}
                            </StatusChip>
                          )}
                        </label>
                        <select
                          id={id}
                          className="ds-select"
                          value={value}
                          disabled={!canBind}
                          onChange={(e) => {
                            setBinding(tpl.ref, e.target.value);
                          }}
                        >
                          <option value="">{t("onboarding.plant.bindings.unbound")}</option>
                          {workCenters.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.code}
                            </option>
                          ))}
                        </select>
                      </li>
                    );
                  })}
                </ul>

                {save.error !== null ? (
                  <WizardProblemNotice
                    error={save.error}
                    onRetry={() => {
                      save.reset();
                    }}
                    titleKey="onboarding.plant.bindings.saveFailedTitle"
                    detailKey="onboarding.plant.bindings.saveFailedBody"
                  />
                ) : null}

                {canBind ? (
                  <div className="plant-commit-actions">
                    <Button
                      busy={save.isPending}
                      disabled={!dirty || save.isPending}
                      onClick={() => {
                        // A REPLACE: every binding we mean to keep is in the body.
                        save.mutate({
                          facility_id: facilityId,
                          bindings: Object.entries(cleanBindings(draft)).map(([ref, wc]) => ({
                            step_template_ref: ref,
                            work_center_id: wc,
                          })),
                        });
                      }}
                    >
                      {save.isPending
                        ? t("onboarding.plant.bindings.saving")
                        : t("onboarding.plant.bindings.save")}
                    </Button>
                    <p className="plant-hint">{t("onboarding.plant.bindings.replaceNote")}</p>
                  </div>
                ) : (
                  <p className="plant-hint">{t("onboarding.plant.bindings.readOnly")}</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function cleanBindings(draft: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(draft).sort()) {
    const value = draft[key];
    if (value !== undefined && value.length > 0) out[key] = value;
  }
  return out;
}
